import React, { useEffect, useState } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import Dashboard from './components/Dashboard';
import SubmissionReview from './components/SubmissionReview';
import AdminPanel from './components/AdminPanel';
import CertLookup from './components/CertLookup';
import DefectClassification from './components/DefectClassification';
import ErrorBoundary from './components/ErrorBoundary';
import { getProfile, saveProfile } from './api/client';

function PortalApp() {
  const { user, signOut } = useAuthenticator();
  const [currentView, setCurrentView] = useState<'dashboard' | 'review' | 'admin' | 'cert-lookup' | 'defect'>('dashboard');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

  const [nickname, setNickname] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');
  const [nicknameSaving, setNicknameSaving] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);

  useEffect(() => {
    getProfile()
      .then(p => {
        if (p.nickname) setNickname(p.nickname);
        if (p.is_admin) setIsAdmin(true);
      })
      .catch(() => {});
  }, []);

  const handleReview = (submissionId: string) => {
    setSelectedSubmissionId(submissionId);
    setCurrentView('review');
  };

  const handleBack = () => {
    setSelectedSubmissionId(null);
    setCurrentView('dashboard');
  };

  const openNicknameEdit = () => {
    setNicknameInput(nickname ?? '');
    setNicknameError(null);
    setEditingNickname(true);
  };

  const handleNicknameSave = async () => {
    const trimmed = nicknameInput.trim();
    if (!trimmed) return;
    setNicknameSaving(true);
    setNicknameError(null);
    try {
      await saveProfile(trimmed);
      setNickname(trimmed);
      setEditingNickname(false);
    } catch (e: any) {
      setNicknameError(e.message);
    } finally {
      setNicknameSaving(false);
    }
  };

  const displayName = nickname ?? user?.signInDetails?.loginId ?? 'Annotator';

  return (
    <div className="portal">
      <header className="portal-header">
        <div className="portal-header-left">
          <h1>AICG v2 — Annotation Portal</h1>
          <nav className="portal-nav">
            <button
              className={`nav-btn ${currentView === 'dashboard' ? 'nav-btn-active' : ''}`}
              onClick={handleBack}
            >
              Dashboard
            </button>
            {isAdmin && (
              <button
                className={`nav-btn ${currentView === 'defect' ? 'nav-btn-active' : ''}`}
                onClick={() => { setSelectedSubmissionId(null); setCurrentView('defect'); }}
              >
                Defect Classification
              </button>
            )}
            {isAdmin && (
              <button
                className={`nav-btn ${currentView === 'admin' ? 'nav-btn-active' : ''}`}
                onClick={() => { setSelectedSubmissionId(null); setCurrentView('admin'); }}
              >
                Admin Audit
              </button>
            )}
            {isAdmin && (
              <button
                className={`nav-btn ${currentView === 'cert-lookup' ? 'nav-btn-active' : ''}`}
                onClick={() => { setSelectedSubmissionId(null); setCurrentView('cert-lookup'); }}
              >
                Cert Lookup
              </button>
            )}
          </nav>
          {currentView === 'review' && (
            <button className="back-btn" onClick={handleBack}>← Back</button>
          )}
        </div>
        <div className="portal-header-right">
          <div className="user-identity">
            <span className="user-display-name">{displayName}</span>
            <button className="nickname-edit-btn" onClick={openNicknameEdit} title="Set display name">✎</button>
          </div>
          <button className="sign-out-btn" onClick={signOut}>Sign Out</button>
        </div>
      </header>

      {editingNickname && (
        <div className="nickname-modal-overlay" onClick={() => setEditingNickname(false)}>
          <div className="nickname-modal" onClick={e => e.stopPropagation()}>
            <h3>Set your display name</h3>
            <p className="nickname-hint">Shown on the leaderboard instead of your email.</p>
            <input
              className="nickname-input"
              type="text"
              value={nicknameInput}
              onChange={e => setNicknameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleNicknameSave(); if (e.key === 'Escape') setEditingNickname(false); }}
              placeholder="Your nickname (max 30 chars)"
              maxLength={30}
              autoFocus
            />
            {nicknameError && <div className="nickname-error">{nicknameError}</div>}
            <div className="nickname-modal-actions">
              <button className="nickname-cancel-btn" onClick={() => setEditingNickname(false)}>Cancel</button>
              <button
                className="nickname-save-btn"
                onClick={handleNicknameSave}
                disabled={!nicknameInput.trim() || nicknameSaving}
              >
                {nicknameSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="portal-main">
        <ErrorBoundary key={currentView === 'review' ? selectedSubmissionId ?? 'review' : currentView}>
          {currentView === 'dashboard' && (
            <Dashboard onReview={handleReview} />
          )}
          {currentView === 'review' && selectedSubmissionId && (
            <SubmissionReview submissionId={selectedSubmissionId} onSaved={handleBack} isAdmin={isAdmin} />
          )}
          {currentView === 'admin' && (
            <AdminPanel onReview={handleReview} />
          )}
          {currentView === 'cert-lookup' && (
            <CertLookup />
          )}
          {currentView === 'defect' && (
            <DefectClassification />
          )}
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Authenticator loginMechanisms={['email']} socialProviders={['google']}>
      <PortalApp />
    </Authenticator>
  );
}
